import z from 'zod';
import {
  DateTimeSchema,
  objectIdRef,
  ObjectIdSchema,
  ObjectIdString,
  OpportunityListQuery,
} from '../common.js';
import { OpportunityStatus } from '../../opportunityTypes.js';
import { UserDTO } from './userSchema.js';

// Define available action types
export const ActionType = z.enum([
  'no_action_needed',
  'messaged_recruiter',
  'coaching_session',
  'escalated_to_leadership',
  'other',
  'marked_read',
  'marked_unread',
  'recruiter_marked_read',
  'status_changed',
  'system_opened',
  'system_closed',
  'owner_reassigned',
  'recruiter_resolved',
  'recruiter_reopened',
]);
export type ActionType = z.infer<typeof ActionType>;
export const actionTypes = ActionType.options;
// Display names for action types
export const ActionTypeLabels: Record<ActionType, string> = {
  no_action_needed: 'Reviewed - No action needed',
  messaged_recruiter: 'Messaged recruiter to follow up',
  coaching_session: 'Provided coaching to recruiter',
  escalated_to_leadership: 'Escalated to leadership',
  other: 'Other',
  marked_read: 'Marked as read',
  marked_unread: 'Marked as unread',
  recruiter_marked_read: 'Recruiter marked as read',
  status_changed: 'Status changed',
  system_opened: 'StaffIQ opened opportunity',
  system_closed: 'StaffIQ auto-closed opportunity',
  owner_reassigned: 'Owner reassigned',
  recruiter_resolved: 'Recruiter resolved opportunity',
  recruiter_reopened: 'Recruiter re-opened opportunity',
};

export const IAction = z.object({
  _id: ObjectIdSchema,
  opportunity: objectIdRef('Opportunity'),
  user: objectIdRef('AuthUser'),
  actionType: ActionType,
  notes: z.string().optional(),
  statusChange: z
    .object({
      from: z.string().optional(),
      to: z.string().optional(),
    })
    .optional(),
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema,
});
export type IAction = z.infer<typeof IAction>;

export const ActionDTO = z.object({
  ...IAction.shape,
  _id: ObjectIdString,
  user: UserDTO.partial(),
  opportunity: ObjectIdString,
});
export type ActionDTO = z.infer<typeof ActionDTO>;

export const GeneralActionInput = z.object({
  actionType: ActionType,
  notes: z.string().optional(),
  newStatus: OpportunityStatus.optional(),
});

export const CreateActionInput = z.object({
  ...GeneralActionInput.shape,
  opportunityId: ObjectIdString,
  accountId: ObjectIdString.optional(),
});
export type CreateActionInput = z.infer<typeof CreateActionInput>;

export const CreateBulkActionInput = z.object({
  ...GeneralActionInput.shape,
  opportunityIds: ObjectIdString.array().optional(),
  query: OpportunityListQuery.optional(),
  fetchedAt: DateTimeSchema,
});
export type CreateBulkActionInput = z.infer<typeof CreateBulkActionInput>;

/**
 * Format action type for display
 */
export function formatActionType(actionType: string): string {
  return ActionTypeLabels[actionType as ActionType] || actionType;
}

/**
 * Get CSS class for action type badge
 * Returns the color name for use with Bootstrap Badge bg prop
 */
export function getActionTypeBadgeClass(actionType: string): string {
  switch (actionType) {
    case 'no_action_needed':
      return 'info';
    case 'messaged_recruiter':
      return 'primary';
    case 'coaching_session':
      return 'warning';
    case 'escalated_to_leadership':
      return 'danger';
    case 'marked_read':
      return 'success';
    case 'marked_unread':
      return 'light';
    case 'status_changed':
      return 'primary';
    case 'owner_reassigned':
      return 'info';
    case 'system_opened':
      return 'secondary';
    case 'system_closed':
      return 'secondary';
    case 'other':
    default:
      return 'secondary';
  }
}
/**
 * Shared action types for the Action Tracking System
 */

// User ID for automated actions taken by the Toro system
export const SYSTEM_USER_ID = '000000000000000000000001';
